import { Injectable, NgZone, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { WidgetChatbot, WidgetConversation, WidgetLogin, WidgetMessagePage, WidgetStreamEvent } from '../dto/widget.dto';

@Injectable({ providedIn: 'root' })
export class WidgetApi {
  private readonly zone = inject(NgZone);
  private apiUrl = '';
  private appId = '';
  private appKey = '';
  private embeddingOrigin = '';
  private storageKey = '';
  private token: WidgetLogin | null = null;

  configure(apiUrl: string, appId: string, appKey: string, embeddingOrigin: string): void {
    this.apiUrl = apiUrl.replace(/\/$/, '');
    this.appId = appId;
    this.appKey = appKey;
    this.embeddingOrigin = embeddingOrigin;
    this.storageKey = `safegpt.widget.session.${appId}.${encodeURIComponent(embeddingOrigin || 'direct')}`;
    this.token = this.readStoredToken();
  }

  async authenticate(): Promise<void> {
    await this.getToken();
  }

  getChatbot(): Promise<WidgetChatbot> {
    return this.request<WidgetChatbot>('/v1/public/getchatbot');
  }

  getMessages(conversationId: string | null): Promise<WidgetMessagePage> {
    return this.request<WidgetMessagePage>(`/v1/public/getmessages${this.pathSegment(conversationId)}`);
  }

  closeConversation(conversationId: string): Promise<WidgetConversation> {
    return this.request<WidgetConversation>(`/v1/public/closeconversation/${conversationId}`);
  }

  submitRating(conversationId: string, rating: number, feedback: string): Promise<WidgetConversation> {
    return this.request<WidgetConversation>(`/v1/public/submitrating/${conversationId}`, {
      method: 'POST',
      body: JSON.stringify({ rating, feedback: feedback.trim() || null }),
    });
  }

  updateRating(conversationId: string, rating: number): Promise<WidgetConversation> {
    return this.request<WidgetConversation>(`/v1/public/updaterating/${conversationId}`, {
      method: 'POST',
      body: JSON.stringify({ rating }),
    });
  }

  requestHumanSupport(conversationId: string | null): Promise<WidgetConversation> {
    return this.request<WidgetConversation>(`/v1/public/requesthumansupport${this.pathSegment(conversationId)}`, {
      method: 'POST', body: '{}',
    });
  }

  trackPageNavigation(conversationId: string, uri: string): Promise<void> {
    return this.request<void>(`/v1/public/trackpagenavigation/${conversationId}`, {
      method: 'POST',
      body: JSON.stringify({ uri }),
    });
  }

  createMessage(conversationId: string | null, message: string, abortController: AbortController, retryLastMessage = false, retryMessageId?: string): Observable<WidgetStreamEvent> {
    const path = `/v1/public/createmessage${this.pathSegment(conversationId)}`;
    return new Observable((observer) => {
      let cancelled = false;
      const start = async (retried: boolean): Promise<void> => {
        try {
          const token = await this.getToken();
          const response = await fetch(`${this.apiUrl}${path}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ message, retryLastMessage, retryMessageId }),
            signal: abortController.signal,
          });
          if (response.status === 401 && !retried) {
            await this.login();
            if (!cancelled) await start(true);
            return;
          }
          if (!response.ok || !response.body) throw new Error(await response.text());
          await this.readSse(response.body, observer);
        } catch (error) {
          if (!cancelled && !abortController.signal.aborted) this.zone.run(() => observer.error(error));
        }
      };
      void start(false);
      return () => { cancelled = true; abortController.abort(); };
    });
  }

  async getAccessToken(): Promise<string> {
    return this.getToken();
  }

  private async request<T>(path: string, init?: RequestInit, retried = false): Promise<T> {
    const token = await this.getToken();
    const headers = new Headers(init?.headers);
    if (!headers.has('Authorization')) headers.set('Authorization', `Bearer ${token}`);
    if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
    const response = await fetch(`${this.apiUrl}${path}`, {
      ...init,
      headers,
      cache: 'no-store',
    });
    if (response.status === 401 && !retried) {
      await this.login();
      return this.request<T>(path, init, true);
    }
    if (!response.ok) throw new Error(`${response.status}:${await response.text()}`);
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  private async getToken(): Promise<string> {
    if (!this.token || Date.parse(this.token.expiration) <= Date.now() + 30_000) await this.login();
    const token = this.token;
    if (!token) throw new Error('Widget login did not return an access token.');
    return token.token;
  }

  private async login(): Promise<void> {
    const response = await fetch(`${this.apiUrl}/v1/public/login/${this.appId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ publicKey: this.appKey, embeddingOrigin: this.embeddingOrigin || null }),
      cache: 'no-store',
    });
    if (!response.ok) throw new Error(`${response.status}:${await response.text()}`);
    this.token = await response.json() as WidgetLogin;
    try { localStorage.setItem(this.storageKey, JSON.stringify(this.token)); } catch { /* Storage is optional. */ }
  }

  private readStoredToken(): WidgetLogin | null {
    try {
      const value = localStorage.getItem(this.storageKey);
      if (!value) return null;
      const token = JSON.parse(value) as WidgetLogin;
      return token.token && Date.parse(token.expiration) > Date.now() + 30_000 ? token : null;
    } catch { return null; }
  }

  private async readSse(body: ReadableStream<Uint8Array>, observer: { next: (event: WidgetStreamEvent) => void; complete: () => void }): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split(/\r?\n\r?\n/);
      buffer = chunks.pop() ?? '';
      for (const chunk of chunks) {
        const event = this.parseSseChunk(chunk);
        if (event) this.zone.run(() => observer.next(event));
      }
    }
    this.zone.run(() => observer.complete());
  }

  private parseSseChunk(chunk: string): WidgetStreamEvent | null {
    const lines = chunk.split(/\r?\n/);
    const name = lines.find((line) => line.startsWith('event:'))?.slice(6).trim();
    const raw = lines.filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trimStart()).join('\n');
    let value: unknown = raw;
    try { value = raw ? JSON.parse(raw) : undefined; } catch { /* Plain text SSE data. */ }
    switch (name) {
      case 'safegpt.conversation.ready': return { type: 'conversation', value };
      case 'safegpt.message.created': return { type: 'created', value };
      case 'safegpt.message.start': return { type: 'messageStart', value };
      case 'safegpt.message.content.part':
      case 'safegpt.message.content.text.delta': return { type: 'message', value };
      case 'safegpt.message.content.done':
      case 'safegpt.message.content.text.done': return { type: 'messageDone', value };
      case 'safegpt.message.file.search':
      case 'safegpt.message.source': return { type: 'source', value };
      case 'safegpt.message.done': return { type: 'completed', value };
      case 'safegpt.message.failed':
      case 'safegpt.run.failed': return { type: 'failed', value };
      default: return null;
    }
  }

  private pathSegment(value: string | null): string {
    return value ? `/${value}` : '';
  }
}
